import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/plugins/counter.css";

const floorPlansData = [
  {
    id: "east-villa",
    title: "East Facing Villa",
    size: "Standard Villa",
    direction: "East",
    images: [
      { src: "/villas-floor-plans/villas-east/villas-east.jpg", label: "3D View" },
      { src: "/villas-floor-plans/villas-east/villas-ground.jpg", label: "Ground Floor" },
      { src: "/villas-floor-plans/villas-east/villas-first.jpg", label: "First Floor" },
    ],
  },
  {
    id: "west-villa",
    title: "West Facing Villa",
    size: "Standard Villa",
    direction: "West",
    images: [
      { src: "/villas-floor-plans/villas-west/villas-west.jpg", label: "3D View" },
      { src: "/villas-floor-plans/villas-west/villas-west-ground.jpg", label: "Ground Floor" },
      { src: "/villas-floor-plans/villas-west/villas-west-first.jpg", label: "First Floor" },
    ],
  },
  {
    id: "450-east-villa",
    title: "450 Sq.Yds East Facing Villa",
    size: "450 Sq.Yds",
    direction: "East",
    images: [
      { src: "/villas-floor-plans/450-Sq-east/450-Sq-east.jpg", label: "3D View" },
      { src: "/villas-floor-plans/450-Sq-east/450-Sq-Yrds-East-ground.jpg", label: "Ground Floor" },
      { src: "/villas-floor-plans/450-Sq-east/450-Sq-Yrds-East-first.jpg", label: "First Floor" },
      { src: "/villas-floor-plans/450-Sq-east/450-Sq-Yrds-East-second.jpg", label: "Second Floor" },
    ],
  },
];

export default function FloorPlansVillas() {
  const [activeTab, setActiveTab] = useState(floorPlansData[0].id);
  const [open, setOpen] = useState(false);
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);

  const activePlan = floorPlansData.find((plan) => plan.id === activeTab) || floorPlansData[0];

  const openGallery = (idx) => {
    setSlides(activePlan.images.map((image) => ({ src: image.src })));
    setIndex(idx);
    setOpen(true);
  };

  return (
    <section className="villas-floorplans-section" id="villas-floor-plans">
      <div className="container">
        <motion.div
          className="villas-floorplans-header"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7 }}
        >
          <span className="villas-floorplans-kicker">Villa Plans</span>
          <h2>Floor Plans</h2>
        </motion.div>

        <div className="villas-floorplans-layout">
          <aside className="villas-floorplans-sidebar" aria-label="Villas floor plan options">
            {floorPlansData.map((plan) => (
              <button
                key={plan.id}
                type="button"
                className={`villas-floorplans-tab ${activeTab === plan.id ? "is-active" : ""}`}
                onClick={() => setActiveTab(plan.id)}
                aria-pressed={activeTab === plan.id}
              >
                <span>{plan.size}</span>
                <small>{plan.direction} Facing</small>
              </button>
            ))}
          </aside>

          <div className="villas-floorplans-content">
            <AnimatePresence mode="wait">
              <motion.div
                key={activePlan.id}
                className="villas-floorplans-shell"
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.35 }}
              >
                <div className="villas-floorplans-summary">
                  <span>{activePlan.direction} Facing</span>
                  <h3>{activePlan.title}</h3>
                  <p>{activePlan.size}</p>
                </div>

                <div className="villas-floorplans-grid">
                  {activePlan.images.map((image, idx) => (
                    <motion.button
                      type="button"
                      className="villas-floorplan-card"
                      key={`${activePlan.id}-${image.label}`}
                      onClick={() => openGallery(idx)}
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.25 }}
                    >
                      <span className="villas-floorplan-media">
                        <img
                          src={image.src}
                          alt={`${activePlan.title} ${image.label}`}
                          loading="lazy"
                        />
                      </span>
                      <span className="villas-floorplan-card-copy">
                        <span>{image.label}</span>
                        <small>View Plan</small>
                      </span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        index={index}
        plugins={[Zoom, Counter, Fullscreen]}
        imageFit="contain"
        zoom={{
          maxZoomPixelRatio: 4,
          zoomInMultiplier: 1.5,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
        }}
        on={{ view: ({ index: currentIndex }) => setIndex(currentIndex) }}
        counter={{
          container: {
            style: { top: "16px", left: "16px", right: "auto", bottom: "auto", fontSize: "14px" },
          },
        }}
        styles={{ container: { backgroundColor: "rgba(7,21,35,0.96)" } }}
      />
    </section>
  );
}
